import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { SubTabBar } from './SubTabBar';
import Businesses from './Businesses';
import CreatorsList from './CreatorsList';
import PendingAccounts from './PendingAccounts';

type Tab = 'businesses' | 'creators';

/** Accounts = REGISTERED users, split by type. Each tab is a funnel: the in-progress
 *  queue (claims / company activations / abandoned signups) sits above the active
 *  roster. There is no separate "pending" tab — pending items live with their type. */
export default function Accounts() {
  const { t } = useTranslation('admin');
  const [tab, setTab] = useState<Tab>('businesses');

  // Tab badges. Same query keys as PendingAccounts → shared react-query cache, no extra fetch.
  const { data: claims } = useSupabaseQuery<{ kind: 'creator' | 'brand' }[]>(
    ['admin-pending-claims'], async () => supabase.rpc('admin_pending_claims'));
  const { data: orgs } = useSupabaseQuery<{ approved: boolean }[]>(
    ['admin-pending-orgs'], async () => supabase.rpc('admin_pending_company_requests'));

  const brandClaims = (claims ?? []).filter((c) => c.kind === 'brand').length;
  const creatorClaims = (claims ?? []).filter((c) => c.kind === 'creator').length;
  // Approved companies re-appear as brand claims, so count only the not-yet-approved
  // ones here to avoid double-counting.
  const companiesNeedAction = (orgs ?? []).filter((o) => !o.approved).length;
  const bizBadge = brandClaims + companiesNeedAction;

  return (
    <>
      <h2 style={{ margin: '0 0 16px' }}>{t('acct.title', { defaultValue: 'Аккаунты' })}</h2>
      <SubTabBar active={tab} onChange={(v) => setTab(v as Tab)} tabs={[
        { v: 'businesses', label: t('acct.businesses', { defaultValue: 'Бизнесы' }) as string, badge: bizBadge || undefined, badgeUrgent: companiesNeedAction > 0 },
        { v: 'creators', label: t('cr.roster', { defaultValue: 'Креаторы' }) as string, badge: creatorClaims || undefined },
      ]} />
      {tab === 'businesses' ? (
        <><PendingAccounts kind="business" /><Businesses /></>
      ) : (
        <><PendingAccounts kind="creator" /><CreatorsList onlyRegistered /></>
      )}
    </>
  );
}
